<i18n src="@/i18n/components/date-picker/date-range-picker-input.json"></i18n>
<template>
  <div
    v-click-outside="onOutSideClick"
    data-cy="date-range-picker"
    class="dateRangePickerInput"
    :style="{ width }"
  >
    <div
      ref="dateRangePickerInputContainer"
      class="dateRangePickerInput__container"
      :class="[
        {
          'dateRangePickerInput__container--foucs':
            startInputFocus || endInputFocus
        },
        {
          'dateRangePickerInput__container--disabled': disabled
        }
      ]"
    >
      <div class="dateRangePickerInput__icon">
        <Icon :icon="icons.Calendar" :color="inputIconColor" :size="14" />
      </div>
      <div class="dateRangePickerInput__field" @click="onStartInputClick">
        <DatePickerReadonlyInput
          ref="startInput"
          :value="dispalyStartInputValue"
          :placeholder="$t('startOfTargetPeriod')"
          :disabled="disabled"
          :focus="startInputFocus"
          :align="inputTextAlign.Center"
        />
      </div>
      <div
        class="dateRangePickerInput__hyphen"
        :class="[
          {
            'dateRangePickerInput__hyphen--disabled': disabled
          }
        ]"
        >-</div
      >
      <div class="dateRangePickerInput__field" @click="onEndInputClick">
        <DatePickerReadonlyInput
          ref="endInput"
          :value="dispalyEndInputValue"
          :placeholder="$t('endOfTargetPeriod')"
          :disabled="disabled"
          :focus="endInputFocus"
          :align="inputTextAlign.Center"
        />
      </div>

      <div class="dateRangePickerInput__deleteButton">
        <Tooltip :active="!disabled" :text="$t('reselectEntirePeriod')">
          <IconButton
            :icon="icons.CloseBold"
            :size="18"
            :icon-scale="0.5"
            :disabled="disabled"
            @click="onDeleteButtonClick"
          />
        </Tooltip>
      </div>
    </div>

    <Transition name="datePickerPopup">
      <div
        v-if="showDatePickerPopup"
        class="dateRangePickerInput__popup"
        :style="popupStyle"
      >
        <div v-if="showPeriodChoices" class="dateRangePickerInput__header">
          <PeriodChoices
            :current="periodChoiceValue"
            @click="onPeriodChoiceClick"
          />
        </div>

        <div class="dateRangePickerInput__body">
          <DateRangePickerPopup
            :date-picker="datePicker"
            :enabled-period="enabledPeriod"
            :selected-period="popupSelectedPeriod"
            :disabled-month-click="true"
            @select-date="onSelectDate"
            @move-year-month="onMoveCalendarButtonClick"
            @mouse-enter-date="onMouseEnterDate"
          />
        </div>
      </div>
    </Transition>
  </div>
</template>

<script lang="ts">
import { Component, Vue, Prop, Emit } from "vue-property-decorator";
import debounce from "lodash/debounce";
import {
  DatePicker,
  PopupAlign,
  InputTextAlign,
  POPUP_WIDTH,
  POPUP_HEIGHT,
  INPUT_HEIGHT,
  INPUT_POPUP_DISTANCE
} from "@/models/date-picker/DatePicker";
import { Calendar } from "@/models/date-picker/Calendar";
import { DateRange, changeDateRange } from "@/components/date-picker/DateRange";
import { PeriodChoice } from "@/components/date-picker/PeriodChoice";
import { DateFormat, formatDate } from "@/util/date-util";
import { getOverflowYStyleElement } from "@/util/common-util";
import { Icons } from "@/const/Icons";
import { Colors } from "@/const/Colors";
import vClickOutside from "v-click-outside";
import Icon from "@/components/Icon.vue";
import IconButton from "@/components/IconButton.vue";
import Tooltip from "@/components/Tooltip.vue";
import DateRangePickerPopup from "@/components/date-picker/popup/DateRangePickerPopup.vue";
import DatePickerReadonlyInput from "@/components/date-picker/DatePickerReadonlyInput.vue";
import PeriodChoices from "@/components/date-picker/PeriodChoices.vue";
import { getCurrentDate } from "@/util/date-util";

const MIN_RIGHT_MARGIN: number = 30;

@Component({
  components: {
    Icon,
    IconButton,
    Tooltip,
    DateRangePickerPopup,
    DatePickerReadonlyInput,
    PeriodChoices
  },
  directives: { clickOutside: vClickOutside.directive }
})
export default class DateRangePickerInput extends Vue {
  @Prop({ type: Object, default: null })
  value!: DateRange;

  @Prop({ type: Boolean, default: false })
  disabled!: boolean;

  @Prop({ type: String, default: "380px" })
  width!: string;

  @Prop({ type: Object, default: null })
  enabledPeriod!: DateRange;

  @Prop({ type: String, default: PopupAlign.Left })
  align!: PopupAlign;

  @Prop({ type: Boolean, default: true })
  showPeriodChoices!: boolean;

  onInput(dateRange: DateRange | null) {
    this.isDelete = false;
    this.$emit("input", dateRange);
  }

  @Emit("delete")
  onDeleteButtonClick() {
    this.isDelete = true;
    this.onInputDelete();
    this.$nextTick(() => {
      this.onOpen();
    });

    this.startInputFocus = true;
    this.endInputFocus = false;
  }

  @Emit("outside-click")
  onOutSideClick() {
    if (this.showDatePickerPopup) {
      this.isDelete = false;
      this.onClose();
      this.onInputDelete();
    }
  }

  datePicker: DatePicker | null = null;

  startInputValue: Date | null = null;
  startInputFocus: boolean = false;

  endInputValue: Date | null = null;
  endInputFocus: boolean = false;

  mouseEnterDate: Date | null = null;

  showDatePickerPopup: boolean = false;
  periodChoiceValue: string = "";
  isDelete: boolean = false;
  isFirstDateSelection: boolean = true;

  saveDate: Date | null = null;
  popupTop: number = 0;
  popupLeft: number = 0;

  icons = Icons;
  colors = Colors;
  inputTextAlign = InputTextAlign;

  debounceSetPopupPosition = debounce(this.setPopupPosition, 600);

  get inputIconColor(): Colors {
    return this.disabled ? Colors.Base500 : Colors.Base600;
  }

  get popupSelectedPeriod(): DateRange | null {
    return this.changeDateRange(
      this.selectedStartInputValue,
      this.selectedEndInputValue
    );
  }

  // 入力欄の開始日の表示を制御
  get selectedStartInputValue(): Date | null {
    // カレンダー選択中の入力欄表示日付
    if (this.startInputFocus) {
      return this.mouseEnterDate;
    }

    // カレンダー選択時の入力欄表示日付（日付マウスオーバーにより日付の切り替わり対応）
    if (this.startInputValue !== null) {
      return this.startInputValue;
    }

    // 保存された日付やinputされた日付
    if (this.value !== null && !this.isDelete) {
      return this.value.min;
    }

    return null;
  }

  // 入力欄の終了日の表示を制御
  get selectedEndInputValue(): Date | null {
    // カレンダー選択中の入力欄表示日付
    if (this.endInputFocus) {
      return this.mouseEnterDate;
    }

    // カレンダー選択時の入力欄表示日付（日付マウスオーバーにより日付の切り替わり対応）
    if (this.endInputValue !== null) {
      return this.endInputValue;
    }

    // 保存された日付やinputされた日付
    if (this.value !== null && !this.isDelete) {
      return this.value.max;
    }

    return null;
  }

  get dispalyStartInputValue(): string {
    return this.selectedStartInputValue !== null
      ? this.formatValue(this.selectedStartInputValue)
      : "";
  }

  get dispalyEndInputValue(): string {
    return this.selectedEndInputValue !== null
      ? this.formatValue(this.selectedEndInputValue)
      : "";
  }

  get dateRangePickerInputContainer(): HTMLElement {
    return this.$refs.dateRangePickerInputContainer as HTMLElement;
  }

  get getOverflowYStyleElement(): Element | null {
    return getOverflowYStyleElement(this.dateRangePickerInputContainer);
  }

  get popupStyle() {
    return {
      width: POPUP_WIDTH + "px",
      height: POPUP_HEIGHT + "px",
      top: this.popupTop + "px",
      left: this.popupLeft + "px"
    };
  }

  mounted() {
    window.addEventListener("resize", this.debounceSetPopupPosition);
    window.addEventListener("scroll", this.setPopupPosition);

    if (this.getOverflowYStyleElement !== null) {
      this.getOverflowYStyleElement.addEventListener(
        "scroll",
        this.debounceSetPopupPosition
      );
    }
  }

  beforeDestroy() {
    window.removeEventListener("resize", this.debounceSetPopupPosition);
    window.removeEventListener("scroll", this.setPopupPosition);

    if (this.getOverflowYStyleElement !== null) {
      this.getOverflowYStyleElement.removeEventListener(
        "scroll",
        this.debounceSetPopupPosition
      );
    }
  }

  setPopupPosition() {
    const clientRect = this.dateRangePickerInputContainer.getBoundingClientRect();

    // POPUP X軸位置調整
    let left = clientRect.left;
    if (this.align === PopupAlign.Center) {
      left = left + clientRect.width / 2 - POPUP_WIDTH / 2;
    } else if (this.align === PopupAlign.Right) {
      left = left + clientRect.width - POPUP_WIDTH;
    }

    // スクロール表示範囲内に留まるようX軸位置調整
    let el = document.getElementById("app");
    if (el != null) {
      let contentRight = el.clientWidth! - window.scrollX;
      if (left + POPUP_WIDTH + MIN_RIGHT_MARGIN > contentRight) {
        left = contentRight - POPUP_WIDTH - MIN_RIGHT_MARGIN;
      }
    }

    // POPUP Y軸位置調整
    let top = clientRect.top;
    const popupBottomPosition =
      top + INPUT_HEIGHT + INPUT_POPUP_DISTANCE + POPUP_HEIGHT;

    if (popupBottomPosition > window.innerHeight) {
      top = top - (INPUT_POPUP_DISTANCE + POPUP_HEIGHT);

      if (top > window.innerHeight - POPUP_HEIGHT) {
        top = window.innerHeight - POPUP_HEIGHT;
      }
    } else {
      top = top + INPUT_HEIGHT + INPUT_POPUP_DISTANCE;
    }

    this.popupTop = top < 0 ? 0 : top;
    this.popupLeft = left;
  }

  // 閉じた時に表示していたカレンダーを開く
  // 未入力時は現在の月を支点としたカレンダーを開く
  // 初めから入力されていたときは入力された日付を支点にカレンダーを開く
  onBuild() {
    if (this.saveDate === null) {
      if (this.value !== null) {
        this.saveDate = this.value.max;
      } else {
        this.saveDate = getCurrentDate();
      }
    }

    this.datePicker = DatePicker.build(this.saveDate);
  }

  onOpen() {
    this.showDatePickerPopup = true;
    this.isFirstDateSelection = true;
    this.onBuild();
    this.setPopupPosition();

    // 保存された日付やinputされた日付がある場合セットする
    if (this.value !== null && !this.isDelete) {
      this.startInputValue = this.value.min;
      this.endInputValue = this.value.max;

      if (this.startInputFocus) {
        this.mouseEnterDate = this.value.min;
      }

      if (this.endInputFocus) {
        this.mouseEnterDate = this.value.max;
      }
    }
  }

  onClose() {
    this.showDatePickerPopup = false;
    this.startInputFocus = false;
    this.endInputFocus = false;
    this.mouseEnterDate = null;
  }

  onDatesInput() {
    this.isFirstDateSelection = false;
    if (
      !(
        this.startInputValue instanceof Date &&
        this.endInputValue instanceof Date
      )
    )
      return;

    this.onInput(
      this.changeDateRange(this.startInputValue, this.endInputValue)
    );
  }

  onStartInputClick() {
    if (!this.disabled) {
      this.startInputFocus = true;
      this.endInputFocus = false;
      this.mouseEnterDate = this.startInputValue;

      if (!this.showDatePickerPopup) {
        this.onOpen();
      }
    }
  }

  onEndInputClick() {
    if (!this.disabled) {
      this.startInputFocus = false;
      this.endInputFocus = true;
      this.mouseEnterDate = this.endInputValue;

      if (!this.showDatePickerPopup) {
        this.onOpen();
      }
    }
  }

  onStartInput(date: Date) {
    this.startInputValue = date;

    if (this.endInputValue === null || this.isFirstDateSelection) {
      this.onEndInputClick();
    } else {
      this.onClose();
    }

    this.onDatesInput();
  }

  onEndInput(date: Date) {
    this.endInputValue = date;

    if (this.startInputValue === null || this.isFirstDateSelection) {
      this.onStartInputClick();
    } else {
      this.onClose();
    }

    this.onDatesInput();
  }

  onMoveCalendarButtonClick(calendar: Calendar) {
    const date = new Date(calendar.year, calendar.month, 0);
    this.saveDate = date;
    this.datePicker = DatePicker.build(date);
  }

  onMouseEnterDate(date: Date) {
    this.mouseEnterDate = date;

    // マウスオーバーしている日付と入力済みの日付が逆転している場合、
    // フォーカスと入力されている日付の位置が逆転する
    if (
      this.startInputFocus &&
      this.endInputValue !== null &&
      this.endInputValue < date
    ) {
      this.endInputFocus = true;
      this.startInputFocus = false;

      this.startInputValue = this.endInputValue;
      this.endInputValue = date;
    } else if (
      this.endInputFocus &&
      this.startInputValue !== null &&
      this.startInputValue > date
    ) {
      this.endInputFocus = false;
      this.startInputFocus = true;

      this.endInputValue = this.startInputValue;
      this.startInputValue = date;
    }
  }

  onSelectDate(date: Date) {
    this.periodChoiceValue = "";

    if (this.startInputFocus) {
      this.onStartInput(date);
      return;
    }

    if (this.endInputFocus) {
      this.onEndInput(date);
      return;
    }
  }

  onPeriodChoiceClick(periodChoice: PeriodChoice) {
    this.startInputValue =
      periodChoice.dateRange.min < this.enabledPeriod.min
        ? this.enabledPeriod.min
        : periodChoice.dateRange.min;
    this.endInputValue =
      periodChoice.dateRange.max > this.enabledPeriod.max
        ? this.enabledPeriod.max
        : periodChoice.dateRange.max;
    this.periodChoiceValue = periodChoice.name;

    this.onDatesInput();
    this.onClose();
  }

  onInputDelete() {
    this.startInputValue = null;
    this.endInputValue = null;
    this.mouseEnterDate = null;
    this.periodChoiceValue = "";
  }

  formatValue(value: Date): string {
    return formatDate(DateFormat.yyyy年M月d日, value);
  }

  changeDateRange(min: Date | null, max: Date | null) {
    if (min instanceof Date && max === null) {
      return changeDateRange([min, min]);
    }

    if (min === null && max instanceof Date) {
      return changeDateRange([max, max]);
    }

    if (min instanceof Date && max instanceof Date) {
      return changeDateRange([min, max]);
    }

    return null;
  }
}
</script>

<style scoped lang="scss">
.dateRangePickerInput {
  position: relative;
}

.dateRangePickerInput__container {
  display: flex;
  align-items: center;
  padding: 2px;
  height: $formPartsHeight;
  border: 1px solid $colorBase700;
  border-radius: $sizeRadius;
  background-color: $colorWhite;

  &--foucs {
    border-color: $colorBlue800;
  }
}

.dateRangePickerInput__container--disabled {
  border-color: $colorGray200;
  background-color: $colorGray200;
}

.dateRangePickerInput__icon {
  padding-left: 3px;
  width: 25px;
  text-align: center;
}

.dateRangePickerInput__field {
  flex: 1;
}

.dateRangePickerInput__hyphen {
  padding: 0 10px;
}

.dateRangePickerInput__hyphen--disabled {
  color: $colorBase600;
}

.dateRangePickerInput__deleteButton {
  margin-right: 5px;
  margin-left: 5px;
  height: 18px;
}

.dateRangePickerInput__popup {
  position: fixed;
  width: 100%;
  border: 1px solid $colorBase400;
  border-radius: $sizeRadius;
  background-color: $colorWhite;
  box-shadow: 0 1px 4px $colorShadow100;
}

.dateRangePickerInput__header {
  padding: 10px;
  border-bottom: 1px solid $colorBase400;
}

.dateRangePickerInput__body {
  padding: 7px 10px 10px;
}

.datePickerPopup-enter-active,
.datePickerPopup-leave-active {
  margin-top: 0;
  opacity: 1;
  transition: opacity 0.3s ease, margin-top 0.3s ease;
}
.datePickerPopup-enter,
.datePickerPopup-leave-to {
  margin-top: -40px;
  opacity: 0;
}
</style>
